import dayjs, { Dayjs } from 'dayjs';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';

import { portalUserAuditLogFilterOptions } from '../store/modules/merchant/types';

const getValidationSchema = () =>
  z
    .object({
      createdAfter: z.preprocess(
        (val) => (val ? dayjs(val as Dayjs).startOf('day') : null),
        z
          .instanceof(dayjs as unknown as typeof Dayjs)
          .optional()
          .nullish(),
      ),
      createdBefore: z.preprocess(
        (val) => (val ? dayjs(val as Dayjs).endOf('day') : null),
        z
          .instanceof(dayjs as unknown as typeof Dayjs)
          .optional()
          .nullish(),
      ),
      actions: z.array(z.enum(portalUserAuditLogFilterOptions)),
    })
    .superRefine((data, ctx) => {
      if (
        data.createdAfter &&
        data.createdBefore &&
        data.createdAfter.isAfter(data.createdBefore)
      ) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'Start date must be before end date',
          path: ['createdAfter'],
        });
      }
    });

type FormValues = z.infer<ReturnType<typeof getValidationSchema>>;

const emptyValues: FormValues = {
  createdAfter: null,
  createdBefore: null,
  actions: [],
};

interface Props {
  initialValues?: Partial<FormValues>;
}

export const usePortalUserAuditLogFiltersForm = ({ initialValues }: Props = {}) =>
  useForm<FormValues>({
    resolver: zodResolver(getValidationSchema()),
    defaultValues: { ...emptyValues, ...initialValues },
    reValidateMode: 'onChange',
  });

export type { FormValues as PortalUserAuditLogFiltersFormValues };
export { emptyValues as portalUserAuditLogFiltersEmptyValues };
